<template>
  <div class="create-bill page--create-bill">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('create-bill.page.by-template-title') }}
        </div>
      </div>
    </div>
    <div class="create-bill__form">
      <div class="create-bill__form-loader" v-if="billTemplateCreating">
        <loader></loader>
      </div>
      <div class="create-bill__form-fields">
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.templateName"
            :title="$t('create-bill.fields.template-name')"
            :placeholder="$t('create-bill.fields.template-name-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field create-bill__form-field--row">
          <custom-select
            class="create-bill__form-field-small"
            v-model:value="billData.lang"
            mode="big"
            value-key="id"
            :title="$t('create-bill.fields.lang')"
            :options="langOptions"
          ></custom-select>
          <text-input
            v-model:value="billData.billNumber"
            :placeholder="$t('create-bill.fields.bill-number-placeholder')"
            :title="$t('create-bill.fields.bill-number')"
          ></text-input>
        </div>
        <div class="create-bill__form-field create-bill__form-field--row">
          <custom-select
            class="create-bill__form-field-small"
            v-model:value="billData.currency"
            mode="big"
            value-key="id"
            :title="$t('create-bill.fields.currency')"
            :options="currencyOptions"
          ></custom-select>
          <text-input
            v-model:value="billData.sum"
            :mask="numberMask"
            :placeholder="$t('create-bill.fields.sum-placeholder')"
            :title="$t('create-bill.fields.sum')"
          ></text-input>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.purpose"
            :title="$t('create-bill.fields.purpose')"
            :hint="$t('create-bill.fields.purpose-hint')"
            :placeholder="$t('create-bill.fields.purpose-placeholder')"
          ></text-input>
        </div>
        <div class="create-bill__form-field">
          <custom-date-picker
            v-model:value="billData.createdAt"
            :disabled="true"
            :label="$t('create-bill.fields.created-at')"
            mode="big"
          ></custom-date-picker>
        </div>
        <div class="create-bill__form-field">
          <custom-date-picker
            v-model:value="billData.expAt"
            :disabled-date-handler="disabledDateHandler"
            :label="$t('create-bill.fields.exp-at')"
            mode="big"
          ></custom-date-picker>
        </div>
        <div class="create-bill__form-field">
          <text-input
            v-model:value="billData.comment"
            :hint="$t('create-bill.fields.comment-hint')"
            :title="$t('create-bill.fields.comment')"
            :placeholder="$t('create-bill.fields.comment-placeholder')"
          ></text-input>
        </div>
      </div>
      <div class="create-bill__form-actions">
        <primary-button
          class="create-bill__form-action"
          :disabled="billTemplateCreating || v$.$silentErrors.length > 0"
          :label="$t('create-bill.actions.create')"
          @click="createBillTemplate"
        ></primary-button>
      </div>
    </div>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import CustomDatePicker from '@vue-app/components/base/input/CustomDatePicker.vue'
import moment from 'moment'
import CheckboxInput from '@vue-app/components/base/input/CheckboxInput.vue'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { CompaniesApi } from '@js-app/office/api/companies-api'

import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Loader from '@vue-app/components/base/Loader.vue'

const numberMask = createNumberMask({
  prefix: '',
  allowDecimal: true,
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ' ',
  allowNegative: false,
  decimalLimit: 2,
})

export default {
  name: 'CreateBillByTemplate',
  props: ['id', 'templateId'],
  components: {
    Loader,
    CheckboxInput,
    CustomDatePicker,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      billData: {
        lang: 'uk',
        currency: 'uah',
        sum: '',
        purpose: '',
        comment: '',
        createdAt: moment().toDate(),
        expAt: moment().add('1', 'weeks').toDate(),
        isCreateTemplate: false,
        templateName: '',
      },
      langOptions: [
        { id: 'uk', name: 'create-bill.options.lang.uk' },
        { id: 'en', name: 'create-bill.options.lang.eng' },
      ],
      currencyOptions: [
        { id: 'uah', name: 'create-bill.options.currency.uah' },
      ],
      numberMask,
      billTemplateCreating: false,
    }
  },
  validations() {
    return {
      billData: {
        sum: { required },
        purpose: { required },
        templateName: { required },
      },
    }
  },
  computed: {
    backLinkRoute() {
      return RouteHelper.getRoute('bills-templates', { companyId: this.id })
    },
  },
  methods: {
    disabledDateHandler(val1) {
      return moment().diff(moment(val1), 'days') > 0
    },
    async createBillTemplate() {
      let formValid = await this.v$.$validate()

      if (!formValid) {
        return
      }

      if (this.billTemplateCreating) {
        return false
      }

      this.billTemplateCreating = true

      let _createBillTemplateResponse = null

      let _requestData = {
        ...this.billData,
        expAt: moment(this.billData.expAt).format('YYYY-MM-DD HH:mm:ss'),
      }

      try {
        _createBillTemplateResponse = await CompaniesApi.createBillTemplate(
          this.id,
          _requestData
        )
      } catch (e) {
        //TODO: Show error toast with error info
        return
      }

      await this.$router.push(
        RouteHelper.getRoute('bill-template-view', {
          companyId: this.id,
          templateId: _createBillTemplateResponse.data.TemplateID,
        })
      )
      this.billTemplateCreating = false
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--create-bill {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.create-bill {
  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 45px 50px 74px;
    width: 100%;
    max-width: 390px;
    position: relative;

    &-loader {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      background: rgba(#ffffff, 0.7);
      z-index: 999;
    }

    &-fields {
      margin-bottom: 19px;
    }

    &-field {
      margin-bottom: 25px;

      &--row {
        display: flex;
        align-items: stretch;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-template {
      margin-bottom: 38px;

      &-name {
        margin-top: 30px;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }
}
</style>
